const DEFAULT_MAP_BASE = 'https://storage.googleapis.com/akoreps-preview-api-map';

type LookupOptions = {
  prNumber: string;
  totalTimeoutMs?: number;
  initialDelayMs?: number;
};

let cachedApiBaseUrl: string | null = null;
let inFlightLookup: Promise<string> | null = null;

function mapBaseUrl(): string {
  return import.meta.env.VITE_PREVIEW_API_MAP_BASE || DEFAULT_MAP_BASE;
}

function timeoutMsFromEnv(): number {
  const raw = import.meta.env.VITE_PREVIEW_API_LOOKUP_TIMEOUT_MS;
  if (!raw) return 120000;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 120000;
}

function wait(delayMs: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });
}

async function doResolve({
  prNumber,
  totalTimeoutMs = timeoutMsFromEnv(),
  initialDelayMs = 1500,
}: LookupOptions): Promise<string> {
  const pointerUrl = `${mapBaseUrl().replace(/\/+$/, '')}/pr-${prNumber}.json`;
  const deadline = Date.now() + totalTimeoutMs;
  let delay = initialDelayMs;
  let lastError: Error | null = null;

  while (Date.now() < deadline) {
    try {
      const response = await fetch(pointerUrl, { cache: 'no-store' });
      if (response.ok) {
        const payload = (await response.json()) as { url?: string };
        if (payload.url) {
          return payload.url;
        }
        lastError = new Error(`Preview pointer missing url field for pr-${prNumber}`);
      } else if (response.status !== 404) {
        lastError = new Error(
          `Preview pointer lookup failed (${response.status}) for pr-${prNumber}`,
        );
      }
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Preview pointer lookup failed');
    }

    if (Date.now() + delay >= deadline) break;
    await wait(delay);
    delay = Math.min(Math.round(delay * 1.6), 8000);
  }

  throw lastError ?? new Error(`Preview API URL not available for pr-${prNumber}`);
}

export async function resolveApiBaseUrl(options: LookupOptions): Promise<string> {
  if (cachedApiBaseUrl) {
    return cachedApiBaseUrl;
  }
  if (inFlightLookup) {
    return inFlightLookup;
  }

  inFlightLookup = doResolve(options)
    .then((resolved) => {
      cachedApiBaseUrl = resolved;
      return resolved;
    })
    .finally(() => {
      inFlightLookup = null;
    });

  return inFlightLookup;
}

export function resetPreviewApiLookupForTests(): void {
  cachedApiBaseUrl = null;
  inFlightLookup = null;
}

/* ------------------------------------------------------------------ */
/*  Shared build-time env constants — single source of truth          */
/* ------------------------------------------------------------------ */

/** Static API URL baked in by Vite. Used in prod, staging, and local dev. */
export const STATIC_API_URL: string = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/** Whether deploy-preview builds resolve the API URL at runtime via a GCS pointer file. */
export const PREVIEW_API_LOOKUP: boolean = import.meta.env.VITE_PREVIEW_API_LOOKUP === 'true';

/** PR number injected by Netlify at build time for deploy previews. */
export const PREVIEW_PR: string | undefined =
  import.meta.env.VITE_PREVIEW_PR_NUMBER?.trim() || undefined;

/**
 * Base URL for raw `fetch` / `fetchEventSource` callers.
 * Uses the same resolution strategy as the axios instance in `api.ts`.
 */
export async function getApiBaseUrlForBrowserFetch(): Promise<string> {
  if (PREVIEW_API_LOOKUP) {
    if (!PREVIEW_PR) {
      throw new Error('Preview API lookup enabled, but VITE_PREVIEW_PR_NUMBER is missing.');
    }
    const url = await resolveApiBaseUrl({ prNumber: PREVIEW_PR });
    return url.replace(/\/+$/, '');
  }
  return STATIC_API_URL.replace(/\/+$/, '');
}
